<!-- 商品发布 - 库存价格 -->
<template>
  <el-form
    ref="formRef"
    v-loading="formLoading"
    :disabled="isDetail"
    :model="formData"
    :rules="rules"
    label-width="120px"
  >
    <el-form-item label="分销类型" prop="subCommissionType">
      <el-radio-group
        v-model="formData.subCommissionType"
        class="w-80"
        @change="changeSubCommissionType"
      >
        <el-radio :value="false">默认设置</el-radio>
        <el-radio :value="true" class="radio">单独设置</el-radio>
      </el-radio-group>
    </el-form-item>
    <el-form-item label="区域代理" prop="areaAgentEnabled">
      <el-switch v-model="formData.areaAgentEnabled" />
      <el-text class="ml-12px" size="small" type="info">开启后该商品参与区域代理分红（默认关）</el-text>
    </el-form-item>
    <el-form-item label="商品返现" prop="cashbackEnabled">
      <el-switch v-model="formData.cashbackEnabled" />
      <el-text class="ml-12px" size="small" type="info">
        确认收货次日起按日返现至余额；退款时从应退货款扣除已发金额
      </el-text>
    </el-form-item>
    <template v-if="formData.cashbackEnabled">
      <el-form-item label="返现总额" prop="cashbackTotalPriceYuan" required>
        <el-input-number
          v-model="formData.cashbackTotalPriceYuan"
          :min="0.01"
          :precision="2"
          :step="1"
          class="!w-200px"
        />
        <el-text class="ml-12px" size="small" type="info">元 / 件；每天到账 ≈ 总额 × 日比例</el-text>
      </el-form-item>
      <el-form-item label="日释放比例" prop="cashbackDailyRatePercent" required>
        <el-input-number
          v-model="formData.cashbackDailyRatePercent"
          :min="0.01"
          :max="100"
          :precision="2"
          :step="1"
          class="!w-200px"
        />
        <el-text class="ml-12px" size="small" type="info">%</el-text>
      </el-form-item>
      <el-form-item v-if="cashbackDailyHint" label="预估日返">
        <el-text type="warning">{{ cashbackDailyHint }}</el-text>
      </el-form-item>
    </template>
    <el-form-item label="商品规格" prop="specType">
      <el-radio-group v-model="formData.specType" class="w-80" @change="onChangeSpec">
        <el-radio :value="false" class="radio">单规格</el-radio>
        <el-radio :value="true">多规格</el-radio>
      </el-radio-group>
    </el-form-item>
    <!-- 多规格添加-->
    <el-form-item v-if="!formData.specType">
      <SkuList
        ref="skuListRef"
        :prop-form-data="formData"
        :property-list="propertyList"
        :rule-config="skuRuleConfig"
      />
    </el-form-item>
    <el-form-item v-if="formData.specType" label="商品属性">
      <el-button class="mb-10px mr-15px" @click="attributesAddFormRef.open">
        添加属性
      </el-button>
      <ProductAttributes
        :is-detail="isDetail"
        :mode="mode"
        :property-list="propertyList"
        @success="generateSkus"
      />
    </el-form-item>
    <template v-if="formData.specType && propertyList.length > 0">
      <el-form-item v-if="!isDetail" label="批量设置">
        <SkuList :is-batch="true" :prop-form-data="formData" :property-list="propertyList" />
      </el-form-item>
      <el-form-item label="规格列表">
        <SkuList
          ref="skuListRef"
          :is-detail="isDetail"
          :prop-form-data="formData"
          :property-list="propertyList"
          :rule-config="skuRuleConfig"
        />
      </el-form-item>
    </template>
  </el-form>

  <!-- 商品属性添加 Form 表单 -->
  <ProductPropertyAddForm ref="attributesAddFormRef" :mode="mode" :propertyList="propertyList" />
</template>
<script lang="ts" setup>
import { PropType, computed, reactive, ref, unref, watch } from 'vue'
import { copyValueToTarget, convertToInteger, formatToFraction } from '@/utils'
import { propTypes } from '@/utils/propTypes'
import {
  getPropertyList,
  PropertyAndValues,
  RuleConfig,
  SkuList
} from '@/views/mall/product/spu/components/index'
import ProductAttributes from './ProductAttributes.vue'
import ProductPropertyAddForm from './ProductPropertyAddForm.vue'
import type { Spu } from '@/api/mall/product/spu'
import { useMessage } from '@/hooks/web/useMessage'

defineOptions({ name: 'ProductSpuSkuForm' })

// sku 相关属性校验规则（外发强制供货结算价）
const skuRuleConfig = computed<RuleConfig[]>(() => {
  // 市场价 / 成本价 / 条码 / 重量 / 体积：非必填，可留空
  const rules: RuleConfig[] = [
    {
      name: 'stock',
      rule: (arg) => arg >= 0,
      message: '商品库存必须大于等于 1 ！！！'
    },
    {
      name: 'price',
      rule: (arg) => arg >= 0.01,
      message: '商品销售价格必须大于等于 0.01 元！！！'
    }
  ]
  if (Number(formData.productSource) === 2 || Number(props.propFormData?.productSource) === 2) {
    rules.push({
      name: 'supplierSettlePrice',
      rule: (arg) => arg != null && Number(arg) >= 0.01,
      message: '外发商品必须填写供货结算价（大于 0）！！！'
    })
  }
  return rules
})

const message = useMessage() // 消息弹窗
const formLoading = ref(false)
const props = defineProps({
  propFormData: {
    type: Object as PropType<Spu>,
    default: () => {}
  },
  isDetail: propTypes.bool.def(false), // 是否作为详情组件
  mode: {
    type: String as PropType<'admin' | 'merchant' | 'supplier'>,
    default: 'admin' // admin=管理后台, merchant/supplier=只读属性
  }
})
const attributesAddFormRef = ref() // 添加商品属性表单
const formRef = ref() // 表单 Ref
const propertyList = ref<PropertyAndValues[]>([]) // 商品属性列表
const skuListRef = ref() // 商品属性列表 Ref
const formData = reactive<Spu & {
  cashbackTotalPriceYuan?: number
  cashbackDailyRatePercent?: number
}>({
  specType: false, // 商品规格
  subCommissionType: false, // 分销类型
  areaAgentEnabled: false, // 区域代理参与开关，默认关
  cashbackEnabled: false,
  cashbackTotalPriceYuan: undefined,
  cashbackDailyRatePercent: undefined,
  productSource: undefined, // 商品来源：1集采 2外发（货品池会传入，必须保留）
  skus: []
})
const rules = reactive({
  specType: [required],
  subCommissionType: [required]
})

/** 预估日返提示 */
const cashbackDailyHint = computed(() => {
  if (!formData.cashbackEnabled) return ''
  const yuan = Number(formData.cashbackTotalPriceYuan)
  const percent = Number(formData.cashbackDailyRatePercent)
  if (!(yuan > 0) || !(percent > 0)) return ''
  const totalFen = Math.round(yuan * 100)
  const rate = Math.round(percent * 100) // 百分数 → 万分比
  const dailyFen = Math.floor((totalFen * rate) / 10000)
  if (dailyFen < 1) {
    return '按当前配置日返现不足 1 分，请调大总额或比例'
  }
  return `约 ${formatToFraction(dailyFen)} 元/天，发完约 ${Math.ceil(totalFen / dailyFen)} 天`
})

/** 将传进来的值赋值给 formData */
watch(
  () => props.propFormData,
  (data) => {
    if (!data) {
      return
    }
    copyValueToTarget(formData, data)
    // 返现：分/万分比 → 元/%
    formData.cashbackEnabled = !!data.cashbackEnabled
    formData.cashbackTotalPriceYuan =
      data.cashbackTotalPrice != null && data.cashbackTotalPrice > 0
        ? Number(formatToFraction(data.cashbackTotalPrice))
        : undefined
    formData.cashbackDailyRatePercent =
      data.cashbackDailyRate != null && data.cashbackDailyRate > 0
        ? data.cashbackDailyRate / 100
        : undefined
    // 将 SKU 的属性，整理成 PropertyAndValues 数组
    propertyList.value = getPropertyList(data)
  },
  {
    immediate: true
  }
)

/** 表单校验 */
const emit = defineEmits(['update:activeName'])
const validate = async () => {
  if (!formRef) return
  try {
    // 校验 sku
    skuListRef.value.validateSku()
    await unref(formRef).validate()
    // 返现：元/% → 分/万分比
    if (formData.cashbackEnabled) {
      const yuan = Number(formData.cashbackTotalPriceYuan)
      const percent = Number(formData.cashbackDailyRatePercent)
      if (!(yuan > 0) || !(percent > 0) || percent > 100) {
        message.error('开启商品返现时，请填写返现总额与日释放比例（0～100%）')
        emit('update:activeName', 'sku')
        throw new Error('cashback config invalid')
      }
      formData.cashbackTotalPrice = convertToInteger(yuan)
      formData.cashbackDailyRate = Math.round(percent * 100)
      const daily = Math.floor((formData.cashbackTotalPrice * formData.cashbackDailyRate) / 10000)
      if (daily < 1) {
        message.error('日返现金额不足 1 分，请调大总额或日比例')
        emit('update:activeName', 'sku')
        throw new Error('cashback daily too small')
      }
    } else {
      formData.cashbackEnabled = false
    }
    const { cashbackTotalPriceYuan: _y, cashbackDailyRatePercent: _p, ...rest } = formData
    Object.assign(props.propFormData, rest)
  } catch (e: any) {
    if (e?.message !== 'cashback config invalid' && e?.message !== 'cashback daily too small') {
      message.error('【库存价格】不完善，请填写相关信息')
    }
    emit('update:activeName', 'sku')
    throw e
  }
}
defineExpose({ validate })

/** 分销类型 */
const changeSubCommissionType = () => {
  // 默认为零，类型切换后也要重置为零
  for (const item of formData.skus!) {
    item.firstBrokeragePercent = 0
    item.secondBrokeragePercent = 0
  }
}

/** 选择规格 */
const onChangeSpec = () => {
  // 重置商品属性列表
  propertyList.value = []
  // 重置sku列表
  formData.skus = [
    {
      name: '', // SKU 名称，提交时会自动使用 SPU 名称
      price: 0,
      marketPrice: undefined,
      costPrice: undefined,
      supplierSettlePrice: undefined,
      barCode: '',
      picUrl: '',
      stock: 0,
      weight: undefined,
      volume: undefined,
      firstBrokeragePercent: 0,
      secondBrokeragePercent: 0
    }
  ]
}

/** 调用 SkuList generateTableData 方法*/
const generateSkus = (propertyList: any[]) => {
  skuListRef.value.generateTableData(propertyList)
}
</script>
